import DOMPurify from 'dompurify';
import type { AddendumDelta, AddendumDeltaItem } from '@/models/report';
import type { NormalizedAddendum } from '../../types/reportDetail';
import { formatDateTime } from '../../utils/formatDateTime';
import styles from './AddendaSection.module.css';

interface AddendaSectionProps {
  addendums: NormalizedAddendum[];
}

export default function AddendaSection({ addendums }: AddendaSectionProps) {
  if (addendums.length === 0) return null;

  return (
    <section className={styles.section} data-section="addenda">
      <h3 className={styles.sectionTitle}>Addenda</h3>
      {addendums.map((a, i) => (
        <div
          key={a.uuid ?? i}
          id={a.reportUuid ? `addendum-report-${a.reportUuid}` : undefined}
          className={styles.addendum}
        >
          <div className={styles.addendumHeader}>
            <span className={styles.addendumLabel}>
              {addendums.length === 1 ? 'Addendum' : `Addendum ${i + 1}`}
            </span>
            <span className={styles.addendumMeta}>
              {a.author} · {formatDateTime(a.createdAt)}
              {a.reason ? ` · ${a.reason}` : ''}
            </span>
          </div>
          {a.text ? <p className={styles.addendumText}>{a.text}</p> : null}
          {a.delta ? <DeltaBlock delta={a.delta} /> : null}
        </div>
      ))}
    </section>
  );
}

function DeltaBlock({ delta }: { delta: AddendumDelta }) {
  const hasAnyChange =
    delta.measurements.length > 0 ||
    delta.findings.length > 0 ||
    delta.flags.length > 0 ||
    delta.conclusion !== null ||
    delta.followUp !== null;

  if (!hasAnyChange) {
    return <p className={styles.noChanges}>No data changes recorded</p>;
  }

  return (
    <div className={styles.delta}>
      {delta.measurements.length > 0 && (
        <DeltaGroup
          title={`Measurements changed (${delta.measurements.length})`}
          items={delta.measurements}
        />
      )}
      {delta.findings.length > 0 && (
        <DeltaGroup
          title={`Findings changed (${delta.findings.length})`}
          items={delta.findings}
        />
      )}
      {delta.flags.length > 0 && (
        <div className={styles.deltaGroup}>
          <div className={styles.deltaGroupTitle}>
            Flags changed ({delta.flags.length})
          </div>
          <ul className={styles.deltaList}>
            {delta.flags.map((f) => (
              <li key={`${f.change}:${f.label}`}>
                {f.change === 'added' ? (
                  <>
                    <span className={styles.deltaArrow}>added</span>{' '}
                    <span className={styles.deltaAfter}>{f.label}</span>
                  </>
                ) : (
                  <>
                    <span className={styles.deltaArrow}>removed</span>{' '}
                    <span className={styles.deltaBefore}>{f.label}</span>
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {delta.conclusion && (
        <div className={styles.deltaGroup}>
          <div className={styles.deltaGroupTitle}>Conclusion changed</div>
          <div
            className={styles.conclusionAfter}
            // eslint-disable-next-line react/no-danger -- sanitized via DOMPurify
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(delta.conclusion.after),
            }}
          />
        </div>
      )}
      {delta.followUp && (
        <div className={styles.deltaGroup}>
          <div className={styles.deltaGroupTitle}>Follow-up changed</div>
          <ul className={styles.deltaList}>
            <li>
              {delta.followUp.before && (
                <span className={styles.deltaBefore}>
                  {delta.followUp.before}
                </span>
              )}{' '}
              <span className={styles.deltaArrow}>→</span>{' '}
              {delta.followUp.after ? (
                <span className={styles.deltaAfter}>
                  {delta.followUp.after}
                </span>
              ) : (
                <span className={styles.deltaArrow}>(cleared)</span>
              )}
            </li>
          </ul>
        </div>
      )}
    </div>
  );
}

function DeltaGroup({
  title,
  items,
}: {
  title: string;
  items: AddendumDeltaItem[];
}) {
  return (
    <div className={styles.deltaGroup}>
      <div className={styles.deltaGroupTitle}>{title}</div>
      <ul className={styles.deltaList}>
        {items.map((m) => (
          <li key={`${m.change}:${m.label}`}>
            <span className={styles.deltaItemLabel}>{m.label}:</span>{' '}
            {m.change === 'added' && (
              <>
                <span className={styles.deltaArrow}>added</span>{' '}
                <span className={styles.deltaAfter}>
                  {m.after}
                  {m.unit ? ` ${m.unit}` : ''}
                </span>
              </>
            )}
            {m.change === 'removed' && (
              <>
                <span className={styles.deltaBefore}>
                  {m.before}
                  {m.unit ? ` ${m.unit}` : ''}
                </span>{' '}
                <span className={styles.deltaArrow}>removed</span>
              </>
            )}
            {m.change === 'modified' && (
              <>
                <span className={styles.deltaBefore}>
                  {m.before}
                  {m.unit ? ` ${m.unit}` : ''}
                </span>{' '}
                <span className={styles.deltaArrow}>→</span>{' '}
                <span className={styles.deltaAfter}>
                  {m.after}
                  {m.unit ? ` ${m.unit}` : ''}
                </span>
              </>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
